// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { Badge } from '@/components/ui/Badge'
import { ROUTES } from '@/constants/routes'
import { queryKeys } from '@/constants/queryKeys'
import { useAuth } from '@/lib/auth/useAuth'
import { apiClient } from '@/lib/api/client'
import { accountApi } from '@/lib/api/account'
import { friendlyError, getErrorMessage } from '@/lib/utils/errorHandler'
import type { AuthTokens, User } from '@/types'

interface InviteDetails {
  id: string
  tenantId: string
  email: string
  role: string
  expiresAt: string
  organization: string
  accountExists: boolean
}

function roleLabel(role: string): string {
  const map: Record<string, string> = { admin: 'Admin', developer: 'Developer', viewer: 'Viewer', member: 'Member' }
  return map[role] ?? role
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { applyAuthResult, user, isAuthenticated, logout } = useAuth()

  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid invite link.')
      setLoading(false)
      return
    }

    apiClient
      .get<InviteDetails>(`/v1/invite/${token}`)
      .then((data) => setInvite(data))
      .catch((err: unknown) => setLoadError(getErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [token])

  const emailMatch =
    Boolean(user && invite && user.email.toLowerCase() === invite.email.toLowerCase())
  const sameOrg = Boolean(user && invite && user.tenantId === invite.tenantId)

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSubmitError('')
    setSubmitting(true)
    try {
      const result = await apiClient.post<{ tokens: AuthTokens; user: User }>('/v1/invite/accept', {
        token,
        name: name.trim(),
        password,
      })
      applyAuthResult(result.tokens, result.user)
      await queryClient.invalidateQueries({ queryKey: queryKeys.account.invitesMine() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.workspaces() })
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      setSubmitError(friendlyError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleAcceptAsExisting = async () => {
    if (!token) return
    setSubmitError('')
    setSubmitting(true)
    try {
      const result = await accountApi.acceptInviteAuthenticated({ token })
      applyAuthResult(result.tokens, result.user)
      await queryClient.invalidateQueries({ queryKey: queryKeys.account.invitesMine() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.workspaces() })
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      setSubmitError(friendlyError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function signOutAndContinue() {
    await logout()
    navigate(ROUTES.LOGIN, { state: { from: location }, replace: true })
  }

  return (
    <AuthLayout>
      <div className="mb-6 text-center">
        <h1 className="text-xl font-bold text-ink">Join organization</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {invite?.accountExists
            ? 'Accept your invitation with your existing account.'
            : 'Accept your invite and create an account.'}
        </p>
      </div>

      {loading && (
        <div className="card flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {!loading && loadError && (
        <div className="card p-6 space-y-4">
          <Alert variant="error">{loadError}</Alert>
          <p className="text-sm text-ink-muted text-center">
            This invite may have expired or already been used.
          </p>
          <div className="text-center">
            <Link to={ROUTES.LOGIN} className="text-sm font-medium text-brand hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      )}

      {!loading && invite && (
        <div className="card p-6 space-y-5">
          <div className="p-3 rounded-lg bg-surface border border-line space-y-1">
            <p className="text-xs text-ink-muted">You have been invited to</p>
            <p className="text-sm font-semibold text-ink">{invite.organization}</p>
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-ink-muted">as</span>
              <Badge variant="default">{roleLabel(invite.role)}</Badge>
            </div>
          </div>

          {invite.accountExists && isAuthenticated && emailMatch && (
            <div className="space-y-4">
              {sameOrg ? (
                <Alert variant="info">
                  You are already in this organization. You can confirm below to clear this invite.
                </Alert>
              ) : (
                <Alert variant="info">
                  You will join <strong>{invite.organization}</strong> and switch to that workspace (you can switch back
                  anytime from the organization menu in the top bar). Other sessions will be signed out.
                </Alert>
              )}
              <Button type="button" className="w-full" loading={submitting} onClick={() => void handleAcceptAsExisting()}>
                {sameOrg ? 'Confirm and continue' : 'Accept invitation'}
              </Button>
              {submitError && (
                <Alert variant="error" title="Could not accept invite">
                  {submitError}
                </Alert>
              )}
            </div>
          )}

          {invite.accountExists && isAuthenticated && !emailMatch && (
            <div className="space-y-4">
              <Alert variant="warning">
                You are signed in as <strong>{user?.email}</strong>. This invite is for{' '}
                <strong>{invite.email}</strong>.
              </Alert>
              <Button type="button" variant="secondary" className="w-full" onClick={() => void signOutAndContinue()}>
                Sign out and sign in with {invite.email}
              </Button>
            </div>
          )}

          {invite.accountExists && !isAuthenticated && (
            <div className="space-y-4">
              <Alert variant="info">
                An account already exists for <strong>{invite.email}</strong>. Sign in to accept — you will stay on this page
                after login if you use the button below.
              </Alert>
              <Button asChild className="w-full">
                <Link to={ROUTES.LOGIN} state={{ from: location }} replace={false}>
                  Sign in to accept
                </Link>
              </Button>
            </div>
          )}

          {!invite.accountExists && (
            <form onSubmit={(e) => void handleCreateSubmit(e)} className="space-y-4">
              {submitError && <Alert variant="error">{submitError}</Alert>}
              <Input label="Email" type="email" value={invite.email} disabled />
              <Input
                label="Your name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder="Full name"
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                hint="Min 8 characters"
              />
              <Button type="submit" className="w-full" loading={submitting}>
                Create account and join
              </Button>
            </form>
          )}

          {!invite.accountExists && (
            <p className="text-center text-xs text-ink-muted">
              Already have an account?{' '}
              <Link to={ROUTES.LOGIN} state={{ from: location }} className="font-medium text-brand hover:underline">
                Sign in
              </Link>
            </p>
          )}
        </div>
      )}
    </AuthLayout>
  )
}
